import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * 退会済みアカウントで再入場したときの利用再開ダイアログ（080 / 設計書 §11.1）。
 *
 * 退会は「データを消さずに全店舗の所属を無効化する」実装のため、退会済みでも
 * 求人リンクから再入場できる。以前は『店舗登録の確認』が出ており、退会済みである
 * ことが伝わらないまま店舗行だけが復活していた（＝退会フラグだけ残る不整合）。
 */
interface AccountReactivationDialogProps {
  open: boolean;
  storeName: string;
  onConfirm: () => Promise<{ success: boolean; error?: string }>;
  onCancel: () => void;
}

const AccountReactivationDialog: React.FC<AccountReactivationDialogProps> = ({
  open,
  storeName,
  onConfirm,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await onConfirm();
      if (!result.success) {
        setError(result.error || '利用の再開に失敗しました');
      }
    } catch {
      setError('利用の再開中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, p: 1 }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', pb: 1 }}>
        退会済みのアカウントです
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          このアカウントは退会済みです。<br />
          再度利用を開始しますか？
        </Typography>
        <Typography variant="body2" color="text.secondary">
          再開すると、退会前のご利用履歴（応募・勤務・報酬）を再び確認できます。
        </Typography>
        {storeName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>{storeName}</strong> の求人も表示されるようになります。
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          sx={{ borderRadius: 6, minWidth: 100 }}
        >
          閉じる
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading}
          sx={{ borderRadius: 6, minWidth: 100 }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : '利用を再開する'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountReactivationDialog;
